'use client';

import { cva, type VariantProps } from 'class-variance-authority';
import { Link } from '@/i18n/routing';
import { ChevronRight } from 'lucide-react';
import * as React from 'react';

import { cn } from '@/lib/utils';

const breadcrumbsVariants = cva('flex items-center', {
  variants: {
    size: {
      sm: 'text-xs space-x-1',
      md: 'text-sm space-x-2',
      lg: 'text-base space-x-3',
    },
    separator: {
      slash: '',
      chevron: '',
      dot: '',
    },
  },
  defaultVariants: {
    size: 'md',
    separator: 'chevron',
  },
});

const breadcrumbLinkVariants = cva('transition-colors duration-200', {
  variants: {
    size: {
      sm: 'text-xs',
      md: 'text-sm',
      lg: 'text-base',
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

export interface BreadcrumbItem {
  label: string;
  href?: string;
}

export interface BreadcrumbsProps extends VariantProps<typeof breadcrumbsVariants> {
  items: BreadcrumbItem[];
  className?: string;
  ariaLabel?: string;
}

export const Breadcrumbs = ({
  items,
  size,
  separator,
  className,
  ariaLabel = 'Breadcrumb',
}: BreadcrumbsProps) => {
  const SeparatorIcon = separator === 'dot' ? () => <span className='mx-2'>•</span> : ChevronRight;
  const separatorClass =
    separator === 'slash'
      ? 'mx-2 text-muted-foreground'
      : separator === 'dot'
        ? ''
        : 'w-4 h-4 text-muted-foreground mx-2';

  return (
    <nav className={cn(breadcrumbsVariants({ size, separator }), className)} aria-label={ariaLabel}>
      {items.map((item, index) => (
        <div key={`${item.label}-${index}`} className='flex items-center'>
          {index > 0 && <SeparatorIcon className={separatorClass} />}
          {item.href ? (
            <Link
              href={item.href}
              className={cn(
                breadcrumbLinkVariants({ size }),
                'text-muted-foreground hover:text-primary',
              )}
            >
              {item.label}
            </Link>
          ) : (
            <span className={cn(breadcrumbLinkVariants({ size }), 'text-foreground font-medium')}>
              {item.label}
            </span>
          )}
        </div>
      ))}
    </nav>
  );
};

export { breadcrumbsVariants, breadcrumbLinkVariants };
